/**
 * 草图持久化 schema v1 类型定义。
 *
 * 设计说明：
 * 1. 本 schema 是草图实体、几何关系跨会话持久化的纯数据协议，供 `.puppy` 离线包草图扩展直接消费；
 * 2. 全部引用使用阶段 1 切片 1 落地的持久 ID（`cad:sketch*:<uuid>`），禁止出现 Three.js 运行时 uuid；
 * 3. 数值使用核心单位：长度米、角度弧度；坐标为世界坐标，与运行时对象存储一致；
 * 4. 确定性要求：payload 不得携带时间戳/随机序号，数组顺序由序列化器按持久 ID 字典序固定；
 * 5. 实体覆盖：点/直线/圆/圆弧/椭圆/抛物线/样条(标准+样式)/槽口(直+弧)/集合(矩形/多边形/闭合链)；
 *    解析器遇到未知 kind 必须显式失败，禁止静默丢弃；
 * 6. 容器语义：槽口/集合的成员边以普通实体记录存在于 entities 中，容器记录按持久 ID 引用成员；
 *    重建时成员边由容器构造函数接管父子关系，不直接挂到草图组。
 */

import type {SketchDimensionSnapshot} from '../dimension/SketchDimensionTypes';
import type {SketchPersistentCircularPatternRecord} from '../../../types/sketch/circular-pattern/SketchCircularPatternTypes';
import type {SketchPersistentMirrorAxisReferenceRecord} from '../../../types/sketch/mirror/SketchMirrorAxisReferenceTypes';

/** 可持久化的普通 JSON 数据（geometryData 等透传载荷）。 */
export type SketchPersistentPlainData =
    | null
    | string
    | number
    | boolean
    | readonly SketchPersistentPlainData[]
    | {readonly [key: string]: SketchPersistentPlainData};

/** 草图持久化 schema v1 版本标识。 */
export const SKETCH_PERSISTENT_DOCUMENT_SCHEMA_VERSION_V1 = 'sketch-persistence-v1';

/** 三维向量元组（世界坐标，米）。 */
export type SketchPersistentVector3 = readonly [number, number, number];

/**
 * 参考平面持久记录。
 * 说明：
 * 1. `referencePlaneId` 保留数值 ID，恢复时经 idOverride 注入，保持 `datum-plane:N` 引用链不断；
 * 2. `hostFace` 仅面驱动草图平面携带，指向宿主实体面的稳定描述。
 */
export interface SketchPersistentReferencePlaneRecord {
    /** 参考平面数值 ID。 */
    readonly referencePlaneId: number;
    /** 显示名称。 */
    readonly displayName: string;
    /** 平面法向（单位向量）。 */
    readonly normal: SketchPersistentVector3;
    /** 平面常量（THREE.Plane constant）。 */
    readonly constant: number;
    /** 平面局部 X 轴（单位向量）。 */
    readonly xAxis: SketchPersistentVector3;
    /** 平面局部 Y 轴（单位向量）。 */
    readonly yAxis: SketchPersistentVector3;
    /** 宿主实体面稳定描述；非面驱动平面为 null。 */
    readonly hostFace: {readonly objectId: string; readonly faceIndex: number} | null;
}

/**
 * 草图点持久记录（拓扑点池）。
 * 说明：
 * 1. 点池收录边端点、独立草图点，以及被其它实体或多条曲线共享的圆心控制点；
 * 2. 私有圆心仍由单条圆/圆弧记录声明，不进入点池，保持既有对象所有权与旧文档兼容；
 * 3. 多个实体引用同一 pointId 时，重建后必须继续共享同一点实例。
 */
export interface SketchPersistentPointRecord {
    /** 点持久 ID。 */
    readonly pointId: string;
    /** 点位（世界坐标，米）。 */
    readonly position: SketchPersistentVector3;
    /** 点类型（controlPoint/sketchPoint，对应 SketchPointKind 值）。 */
    readonly pointKind: string;
    /** 框选角色（SketchPointBoxSelectionRole 有限角色值）；默认角色时省略。 */
    readonly boxSelectionRole?: string;
    /** 是否为辅助对象；false 时省略。 */
    readonly isAuxiliaryObject?: boolean;
}

/**
 * 直线持久记录。
 */
export interface SketchPersistentLineRecord {
    /** 实体持久 ID。 */
    readonly entityId: string;
    /** 实体类型判别标记。 */
    readonly kind: 'line';
    /** 起点持久 ID（指向点池）。 */
    readonly startPointId: string;
    /** 终点持久 ID（指向点池）。 */
    readonly endPointId: string;
    /** 是否为构造线。 */
    readonly isConstruction: boolean;
    /** 是否为无限长直线。 */
    readonly isInfiniteLength: boolean;
    /** 是否为中心线（专用尺寸语义，区别于普通构造线）；false 时省略。 */
    readonly isCenterline?: boolean;
    /** 是否为辅助对象；false 时省略。 */
    readonly isAuxiliaryObject?: boolean;
}

/**
 * 圆持久记录。
 */
export interface SketchPersistentCircleRecord {
    /** 实体持久 ID。 */
    readonly entityId: string;
    /** 实体类型判别标记。 */
    readonly kind: 'circle';
    /** 圆心（世界坐标，米）。 */
    readonly centerWorld: SketchPersistentVector3;
    /** 半径（米）。 */
    readonly radius: number;
    /** 圆心控制点持久 ID；命中点池时为共享引用，否则为该圆的私有圆心声明。 */
    readonly centerPointId: string;
    /** 是否为构造线。 */
    readonly isConstruction: boolean;
}

/**
 * 圆弧持久记录。
 */
export interface SketchPersistentArcRecord {
    /** 实体持久 ID。 */
    readonly entityId: string;
    /** 实体类型判别标记。 */
    readonly kind: 'arc';
    /** 圆心（世界坐标，米）。 */
    readonly centerWorld: SketchPersistentVector3;
    /** 半径（米）。 */
    readonly radius: number;
    /** 起始角（弧度，参考面局部坐标系）。 */
    readonly startAngle: number;
    /** 终止角（弧度）。 */
    readonly endAngle: number;
    /** 是否顺时针。 */
    readonly clockwise: boolean;
    /** 采样段数。 */
    readonly segments: number;
    /** 起点持久 ID（指向点池）。 */
    readonly startPointId: string;
    /** 终点持久 ID（指向点池）。 */
    readonly endPointId: string;
    /** 圆心控制点持久 ID；命中点池时为共享引用，否则为该圆弧的私有圆心声明。 */
    readonly centerPointId: string;
    /** 是否为构造线。 */
    readonly isConstruction: boolean;
}

/**
 * 椭圆持久记录。
 */
export interface SketchPersistentEllipseRecord {
    /** 实体持久 ID。 */
    readonly entityId: string;
    /** 实体类型判别标记。 */
    readonly kind: 'ellipse';
    /** 圆心（世界坐标，米）。 */
    readonly centerWorld: SketchPersistentVector3;
    /** 长半轴半径（米）。 */
    readonly radiusX: number;
    /** 短半轴半径（米）。 */
    readonly radiusY: number;
    /** 整体旋转角（弧度）。 */
    readonly rotation: number;
    /** 起始角（弧度）。 */
    readonly startAngle: number;
    /** 终止角（弧度）。 */
    readonly endAngle: number;
    /** 是否顺时针。 */
    readonly clockwise: boolean;
    /** 采样段数。 */
    readonly segments: number;
    /** 起点控制点持久 ID（指向点池）。 */
    readonly startPointId: string;
    /** 终点控制点持久 ID（指向点池）。 */
    readonly endPointId: string;
    /** 中心点持久 ID（指向点池）。 */
    readonly centerPointId: string;
    /** 四向基准轴控制点（role 为 EllipseControlPointRole 枚举值）。 */
    readonly cardinalControlPoints: readonly {readonly role: string; readonly pointId: string}[];
    /** 是否为构造线。 */
    readonly isConstruction: boolean;
    /** 是否携带闭合轮廓（重建后 syncContour）。 */
    readonly hasContour: boolean;
}

/**
 * 抛物线持久记录。
 */
export interface SketchPersistentParabolaRecord {
    /** 实体持久 ID。 */
    readonly entityId: string;
    /** 实体类型判别标记。 */
    readonly kind: 'parabola';
    /** 焦点（世界坐标，米）。 */
    readonly focalWorld: SketchPersistentVector3;
    /** 顶点（世界坐标，米）。 */
    readonly apexWorld: SketchPersistentVector3;
    /** 起点参数。 */
    readonly startParameter: number;
    /** 终点参数。 */
    readonly endParameter: number;
    /** 采样段数。 */
    readonly segments: number;
    /** 焦点控制点持久 ID（指向点池）。 */
    readonly focalPointId: string;
    /** 顶点控制点持久 ID（指向点池）。 */
    readonly apexPointId: string;
    /** 起点控制点持久 ID（指向点池）。 */
    readonly startPointId: string;
    /** 终点控制点持久 ID（指向点池）。 */
    readonly endPointId: string;
    /** 是否为构造线。 */
    readonly isConstruction: boolean;
}

/**
 * 样条曲线元数据持久记录（结构透传，枚举值由运行时对象消费）。
 */
export interface SketchPersistentSplineMetadataRecord {
    /** 选中后属性面板分类。 */
    readonly selectionKind: string;
    /** 曲线重建类型。 */
    readonly curveKind: string;
    /** 曲线闭合状态。 */
    readonly closureKind: string;
    /** 样式样条模式；非样式样条为 null。 */
    readonly styleSplineMode: string | null;
    /** 曲线阶数；不适用时为 null。 */
    readonly degree: number | null;
    /** 控制点权重列表。 */
    readonly weights: readonly number[];
    /** 是否理性曲线。 */
    readonly isRational: boolean;
}

/**
 * 样条（标准/样式）持久记录。
 */
export interface SketchPersistentSplineRecord {
    /** 实体持久 ID。 */
    readonly entityId: string;
    /** 实体类型判别标记。 */
    readonly kind: 'spline';
    /** 样条变体：standard=SketchSplineObject，style=SketchStyleSplineObject。 */
    readonly variant: 'standard' | 'style';
    /** 全量控制点持久 ID 列表（首=起点，末=终点，中间为内部控制点）。 */
    readonly controlPointIds: readonly string[];
    /** 采样曲线点列表（世界坐标，米）；重建时直接恢复显示，避免异步曲线重建。 */
    readonly curvePoints: readonly SketchPersistentVector3[];
    /** 曲线元数据。 */
    readonly curveMetadata: SketchPersistentSplineMetadataRecord;
    /** Worker 导出的权威 B 样条求值定义；旧文档可缺省。 */
    readonly curveEvaluationDefinition?: SketchBSplineEvaluationDefinition;
    /** 是否为构造线。 */
    readonly isConstruction: boolean;
}

/**
 * 槽口持久记录。
 * 说明：
 * 1. 成员边（顶/底线、内/外弧、端帽弧、构造中心线）以普通实体记录存在于 entities，按 ID 引用；
 * 2. 接合点与各控制点指向点池，重建时由槽口构造函数接管共享拓扑。
 */
export interface SketchPersistentSlotRecord {
    /** 实体持久 ID。 */
    readonly entityId: string;
    /** 实体类型判别标记。 */
    readonly kind: 'slot';
    /** 槽口绘制模式（SketchSlotCommand 枚举值）。 */
    readonly mode: string;
    /** 槽口变体。 */
    readonly slotKind: 'straight' | 'arc';
    /** 起始端帽弧实体 ID。 */
    readonly startCapArcId: string;
    /** 终止端帽弧实体 ID。 */
    readonly endCapArcId: string;
    /** 顶部直线实体 ID（仅直槽口）。 */
    readonly topLineId?: string;
    /** 底部直线实体 ID（仅直槽口）。 */
    readonly bottomLineId?: string;
    /** 构造中心线实体 ID（仅直槽口）。 */
    readonly constructionCenterlineId?: string;
    /** 中心线起始控制点 ID（仅直槽口，指向点池）。 */
    readonly centerlineStartPointId?: string;
    /** 中心线终止控制点 ID（仅直槽口，指向点池）。 */
    readonly centerlineEndPointId?: string;
    /** 中心控制点 ID（仅直槽口，指向点池）。 */
    readonly centerPointId?: string;
    /** 外弧实体 ID（仅弧槽口）。 */
    readonly outerArcId?: string;
    /** 内弧实体 ID（仅弧槽口）。 */
    readonly innerArcId?: string;
    /** 构造中心线弧实体 ID（仅弧槽口；旧文档可缺省，重建时按几何数据合成）。 */
    readonly constructionCenterlineArcId?: string;
    /** 弧线圆心控制点 ID（仅弧槽口，指向点池）。 */
    readonly arcCenterPointId?: string;
    /** 中心线弧中点控制点 ID（仅弧槽口，指向点池）。 */
    readonly centerArcMidpointId?: string;
    /** 4 个共享接合点 ID（指向点池）。 */
    readonly junctionPointIds: {
        readonly p1: string;
        readonly p2: string;
        readonly p3: string;
        readonly p4: string;
    };
    /** 槽口几何元数据（尺寸标注等），透传 JSON。 */
    readonly geometryData: SketchPersistentPlainData;
    /** 是否为构造线。 */
    readonly isConstruction: boolean;
    /** 是否携带闭合轮廓（重建后 syncContour）。 */
    readonly hasContour: boolean;
}

/**
 * 集合（矩形/多边形/闭合链宿主）持久记录。
 */
export interface SketchPersistentCollectionRecord {
    /** 实体持久 ID。 */
    readonly entityId: string;
    /** 实体类型判别标记。 */
    readonly kind: 'collection';
    /** 创建来源（SketchCollectionCreationSource 枚举值）。 */
    readonly creationSource: string;
    /** 集合源边实体 ID 列表（保持闭合链顺序）。 */
    readonly sourceEntityIds: readonly string[];
    /** 集合直接子边实体 ID 列表（如多边形辅助圆、矩形构造对角线）。 */
    readonly directChildEntityIds: readonly string[];
    /** 集合直接子点持久 ID 列表（如矩形构造线中心草图点，指向点池）。 */
    readonly directChildPointIds: readonly string[];
    /** 集合几何元数据，透传 JSON。 */
    readonly geometryData: SketchPersistentPlainData;
    /** 是否携带闭合轮廓（重建后 syncContour）。 */
    readonly hasContour: boolean;
}

/** 草图实体持久记录联合类型。 */
export type SketchPersistentEntityRecord =
    | SketchPersistentLineRecord
    | SketchPersistentCircleRecord
    | SketchPersistentArcRecord
    | SketchPersistentEllipseRecord
    | SketchPersistentParabolaRecord
    | SketchPersistentSplineRecord
    | SketchPersistentSlotRecord
    | SketchPersistentCollectionRecord;

/**
 * 几何关系持久记录。
 * 说明：
 * 1. `relationKind` 为 `SketchRelationKind` 有限联合值；
 * 2. `targetIds` 保持约束绑定顺序（部分关系语义依赖目标顺序）；
 * 3. `numericValue` 承载 Distance/Radius 等数值关系的锁定值；无数值语义或尚未锁定时为 null；
 * 4. `suppressed` 保存用户抑制状态，旧 v1 文档缺省按 false 迁移。
 */
export interface SketchPersistentRelationRecord {
    /** 关系持久 ID。 */
    readonly constraintId: string;
    /** 关系类型（SketchRelationKind 有限联合值）。 */
    readonly relationKind: string;
    /** 目标持久 ID 列表（点池 ID、实体 ID 或私有圆心点 ID）。 */
    readonly targetIds: readonly string[];
    /** 数值关系锁定值（米/弧度）；无数值语义或未锁定时为 null。 */
    readonly numericValue: number | null;
    /** 是否保留定义但排除活动求解。 */
    readonly suppressed?: boolean;
    /** 稳定多解分支提示；无分支语义时为 null。 */
    readonly branchHint?: SketchPersistentPlainData | null;
    /** 关系参数公式需要的额外纯数据载荷。 */
    readonly formulaPayload?: SketchPersistentPlainData | null;
}

/**
 * 单个草图的持久化文档。
 */
export interface SketchPersistentDocument {
    /** schema 版本标识。 */
    readonly schemaVersion: typeof SKETCH_PERSISTENT_DOCUMENT_SCHEMA_VERSION_V1;
    /** 草图持久 ID。 */
    readonly sketchId: string;
    /** 草图显示名称。 */
    readonly name: string;
    /** 参考平面记录。 */
    readonly referencePlane: SketchPersistentReferencePlaneRecord;
    /** 拓扑点池（按 pointId 字典序）。 */
    readonly points: readonly SketchPersistentPointRecord[];
    /** 实体列表（按 entityId 字典序）。 */
    readonly entities: readonly SketchPersistentEntityRecord[];
    /** 几何关系列表（按 constraintId 字典序）。 */
    readonly relations: readonly SketchPersistentRelationRecord[];
    /**
     * 圆周草图阵列定义列表（按 persistentId 字典序）。
     *
     * 这是 v1 的可选兼容扩展字段；旧文档缺省时视为没有圆周草图阵列。
     */
    readonly circularPatterns?: readonly SketchPersistentCircularPatternRecord[];
    /**
     * 外部镜像轴关系目标。
     * 缺省表示旧版 v1 文档没有外部镜像轴；记录只保存稳定来源，不保存一次性世界轴。
     */
    readonly mirrorAxisReferences?: readonly SketchPersistentMirrorAxisReferenceRecord[];
    /**
     * 尺寸快照列表（按 dimensionId 字典序）。
     *
     * 说明：
     * 1. v1 兼容扩展字段：无尺寸时整个字段省略，旧包缺省视为无尺寸，不触发 schema 升版；
     * 2. 记录复用尺寸领域完整快照（`SketchDimensionSnapshot`），引用一律持久 ID，
     *    悬空尺寸（dangling + repair）允许持久化，加载时保持悬空状态等待 Reattach；
     * 3. 加载顺序在实体与几何关系之后（02 计划 §9.2）。
     */
    readonly dimensions?: readonly SketchDimensionSnapshot[];
}
